import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  Tag, Trash2, Plus, X, Save, Loader2, Edit2, Check, Copy,
  Type, Hash, AlignLeft, Calendar, DollarSign,
  CheckSquare, Image as ImageIcon, ChevronDown, ListChecks, Layers, Upload, Eye, ArrowLeft, Search,
} from "lucide-react";
import { toast } from "sonner";
import { addCleanupEvent } from "@/lib/cleanupDigest";
import { apiClient } from "@/lib/api";
import { resolveBackHref } from "@/lib/navTracker";
import { useCollections } from "@/context/CollectionContext";
import EmptyState from "@/components/EmptyState";
import BottomNav from "@/components/BottomNav";
import { GRAPE_VARIETIES } from "@/lib/wineFields";

/**
 * Categories — Step 3 rebuild.
 *
 * Single inline screen.
 *  - Top: list of existing categories with delete buttons.
 *  - Bottom: form to create a new category (name + description + add-field modal).
 *  - BottomNav links to Collections + Items.
 *
 * No item creation, no collection linking, no per-card edit screen (yet).
 */

const FIELD_TYPES = [
  { value: "text",         label: "Text",         Icon: Type },
  { value: "textarea",     label: "Long Text",    Icon: AlignLeft },
  { value: "number",       label: "Number",       Icon: Hash },
  { value: "currency",     label: "Currency",     Icon: DollarSign },
  { value: "date",         label: "Date",         Icon: Calendar },
  { value: "dropdown",     label: "Dropdown",     Icon: ChevronDown },
  { value: "multi_select", label: "Multi-Select", Icon: ListChecks },
  { value: "checkbox",     label: "Checkbox",     Icon: CheckSquare },
  { value: "image",        label: "Image",        Icon: ImageIcon },
];

// Curated field packs for fast category bootstrapping. Selecting a preset
// replaces the pending field list on the create form. Apply uses the existing
// saveCategory flow (no new schema logic added).
const CATEGORY_PRESETS = {
  wine: {
    label: "Wine",
    // 2026-02-26 — synced 1-for-1 with backend TYPE_MASTER_TEMPLATES["wine"]
    // (20 fields incl. Cuvée). Field order matches the master template so
    // Reset-to-Preset recreates the same schema the seed path installs.
    fields: [
      { field_name: "Producer",         field_type: "text",         required: false, options: [] },
      { field_name: "Region",           field_type: "text",         required: false, options: [] },
      { field_name: "Appellation",      field_type: "text",         required: false, options: [] },
      { field_name: "Cuvée",            field_type: "text",         required: false, options: [] },
      { field_name: "Grape(s)",         field_type: "multi_select", required: false, options: GRAPE_VARIETIES },
      { field_name: "Vintage",          field_type: "number",       required: false, options: [] },
      { field_name: "ABV",              field_type: "text",         required: false, options: [] },
      { field_name: "Bottle Size",      field_type: "text",         required: false, options: [] },
      { field_name: "Item Name",        field_type: "text",         required: true,  options: [] },
      { field_name: "Country",          field_type: "text",         required: false, options: [] },
      { field_name: "Sub-Region",       field_type: "text",         required: false, options: [] },
      { field_name: "Type",             field_type: "dropdown",     required: false, options: ["Red", "White", "Rosé", "Sparkling", "Dessert", "Fortified"] },
      { field_name: "Classification",   field_type: "text",         required: false, options: [] },
      { field_name: "Drink From",       field_type: "number",       required: false, options: [] },
      { field_name: "Drink To",         field_type: "number",       required: false, options: [] },
      { field_name: "Tasting Notes",    field_type: "textarea",     required: false, options: [] },
      { field_name: "Personal Notes",   field_type: "textarea",     required: false, options: [] },
      { field_name: "Storage Location", field_type: "text",         required: false, options: [] },
      { field_name: "Purchase Date",    field_type: "date",         required: false, options: [] },
      { field_name: "Purchase Source",  field_type: "text",         required: false, options: [] },
    ],
  },
  whisky: {
    label: "Whisky",
    // P1 field-grouping fix: heavy-use identity fields (Distillery → Region
    // → Age → ABV) surface at the top so Whisky entry starts with the
    // fields users touch every time. No fields added, renamed, or removed —
    // order only.
    fields: [
      { field_name: "Distillery",      field_type: "text",     required: false, options: [] },
      { field_name: "Region",          field_type: "text",     required: false, options: [] },
      { field_name: "Age",             field_type: "number",   required: false, options: [] },
      { field_name: "ABV",             field_type: "number",   required: false, options: [] },
      { field_name: "Item Name",       field_type: "text",     required: true,  options: [] },
      { field_name: "Country",         field_type: "text",     required: false, options: [] },
      { field_name: "Cask Type",       field_type: "text",     required: false, options: [] },
      { field_name: "Batch / Release", field_type: "text",     required: false, options: [] },
      { field_name: "Bottle Size",     field_type: "text",     required: false, options: [] },
      { field_name: "Purchase Price",  field_type: "currency", required: false, options: [] },
      { field_name: "Estimated Value", field_type: "currency", required: false, options: [] },
      { field_name: "Rating",          field_type: "number",   required: false, options: [] },
      { field_name: "Notes",           field_type: "textarea", required: false, options: [] },
      { field_name: "Image",           field_type: "image",    required: false, options: [] },
    ],
  },
  spirits: {
    label: "Spirits",
    fields: [
      { field_name: "Item Name",        field_type: "text",     required: true,  options: [] },
      { field_name: "Brand / Producer", field_type: "text",     required: false, options: [] },
      { field_name: "Spirit Type",      field_type: "dropdown", required: false, options: ["Gin", "Rum", "Vodka", "Tequila", "Mezcal", "Cognac", "Brandy", "Liqueur", "Bitters", "Other"] },
      { field_name: "Country",          field_type: "text",     required: false, options: [] },
      { field_name: "Region",           field_type: "text",     required: false, options: [] },
      { field_name: "ABV",              field_type: "number",   required: false, options: [] },
      { field_name: "Bottle Size",      field_type: "text",     required: false, options: [] },
      { field_name: "Purchase Price",   field_type: "currency", required: false, options: [] },
      { field_name: "Estimated Value",  field_type: "currency", required: false, options: [] },
      { field_name: "Rating",           field_type: "number",   required: false, options: [] },
      { field_name: "Notes",            field_type: "textarea", required: false, options: [] },
      { field_name: "Image",            field_type: "image",    required: false, options: [] },
    ],
  },
};


export default function Categories() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // When the user landed here via an empty-state CTA (e.g. ItemEntry's
  // "Create a category" button), `?return_to=...` tells us where to send
  // them back after a successful create.
  const returnTo = searchParams.get("return_to") || null;
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const { collections } = useCollections();

  // New-category form
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [collectionId, setCollectionId] = useState("");
  const [pendingFields, setPendingFields] = useState([]);
  // Most-recently-saved category on this session — drives the
  // conditional "Manage Fields" deep link in the gold guidance panel.
  const [lastSavedCatId, setLastSavedCatId] = useState(null);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [saving, setSaving] = useState(false);
  // Preset selector — replaces pendingFields with a curated schema.
  const [presetKey, setPresetKey] = useState("");

  // Derived Create-form validation state. Explicitly re-computed on
  // every render so React always reflects the latest name /
  // collectionId values. Save enables the moment both are non-empty
  // — custom fields are optional and are attached during save when
  // present.
  const createSaveDisabled = saving || !name.trim() || !String(collectionId || "").trim();

  // Inline edit state
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editCollectionId, setEditCollectionId] = useState("");
  const [editImage, setEditImage] = useState("");
  const [editImageTypeError, setEditImageTypeError] = useState("");
  const [editImageDirty, setEditImageDirty] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const editImageInputRef = useRef(null);

  // Two-step delete confirm
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Duplicate state
  const [duplicatingId, setDuplicatingId] = useState(null);
  const duplicate = async (id) => {
    if (duplicatingId) return;
    setEditingId(null);
    setConfirmDeleteId(null);
    setDuplicatingId(id);
    try {
      const { data } = await apiClient.post(`/categories/${id}/duplicate`);
      toast.success(`Category duplicated as "${data.category_name}".`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Duplicate failed.");
    } finally {
      setDuplicatingId(null);
    }
  };

  // List filter: scope categories by collection. Persisted in the URL
  // (`?collection_id=…`) so the selection survives Back / Forward / Refresh
  // and never resurrects as a stale value when the user navigates away and
  // returns. All writes use `replace: true` so the filter never pushes a
  // new history entry (preventing the loop reported by the user).
  const filterCollectionId = searchParams.get("collection_id") || "";
  const writeFilterCollectionId = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v) next.set("collection_id", v);
    else next.delete("collection_id");
    setSearchParams(next, { replace: true });
  };

  // ---- Search + Autosuggest ------------------------------------------
  // Live free-text search across category name and description. Drives
  // BOTH (a) the autosuggest dropdown directly under the input — clicking
  // a suggestion opens that category in the builder — and (b) the
  // underlying visible list below. Mirrors the Collections / Items page
  // search styling (leading magnifier icon + strong border).
  const [searchInput, setSearchInput] = useState("");
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [activeSuggestIdx, setActiveSuggestIdx] = useState(-1);
  const searchWrapRef = useRef(null);
  useEffect(() => {
    if (!suggestOpen) return;
    const onDown = (e) => {
      if (!searchWrapRef.current) return;
      if (!searchWrapRef.current.contains(e.target)) setSuggestOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setSuggestOpen(false); };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [suggestOpen]);

  const matchCategorySearch = (c, q) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return (
      String(c.category_name || "").toLowerCase().includes(needle) ||
      String(c.category_description || "").toLowerCase().includes(needle)
    );
  };

  const openCategorySuggestion = (id) => {
    setSuggestOpen(false);
    setSearchInput("");
    // Categories route table only exposes `/categories/:id/edit` (the
    // builder) — same destination the existing tile-click uses, so the
    // suggestion behaviour matches the rest of the page.
    navigate(`/categories/${id}/edit`);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/categories");
      setCats(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get("/categories");
        if (!cancelled) setCats(data || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const resetForm = () => {
    setName("");
    setDescription("");
    setCollectionId("");
    setPendingFields([]);
    setPresetKey("");
  };

  const applyPreset = (key) => {
    setPresetKey(key);
    if (!key) return;
    const preset = CATEGORY_PRESETS[key];
    if (!preset) return;
    if (pendingFields.length > 0) {
      const ok = window.confirm(
        `Apply the "${preset.label}" preset?\n\nThis will REPLACE your current field list (${pendingFields.length} field${pendingFields.length === 1 ? "" : "s"}).`
      );
      if (!ok) { setPresetKey(""); return; }
    }
    setPendingFields(preset.fields.map((f) => ({ ...f, options: [...(f.options || [])] })));
    toast.success(`Applied "${preset.label}" preset (${preset.fields.length} fields).`);
  };

  // Create-Category modal: auto-apply the matching master preset the
  // moment the user picks a collection so the field list is populated
  // immediately (no separate "Reset to Preset" click, no manual preset
  // dropdown selection). Wine / Whisky / Spirits collections load their
  // curated field pack; anything else (Custom / Premium) leaves the
  // field list empty and flips the preset state to "custom" so a
  // preset dropdown — if surfaced elsewhere — reflects Custom.
  // Scope-limited to this modal: does not touch Manage Fields,
  // Category Details, or the preset engine.
  const onCollectionChange = (id) => {
    setCollectionId(id);
    const col = collections.find((c) => c.id === id);
    const tid = String(col?.type_id || "").toLowerCase();
    setPresetKey(tid); // keep existing semantics

    const preset = CATEGORY_PRESETS[tid];

    if (preset) {
      setPendingFields(
        preset.fields.map((f) => ({ ...f, isPreset: true }))
      );
    } else {
      setPendingFields([]);
    }
  };

  const addPendingField = (field) => {
    setPendingFields((prev) => [...prev, field]);
    setShowFieldModal(false);
  };

  const removePendingField = (idx) => {
    setPendingFields((prev) => prev.filter((_, i) => i !== idx));
  };

  const saveCategory = async () => {
    if (!name.trim()) { toast.error("Category name is required."); return; }
    if (!collectionId) { toast.error("Please pick a collection for this category."); return; }
    setSaving(true);
    try {
      const { data: cat } = await apiClient.post("/categories", {
        category_name: name.trim(),
        category_description: description.trim(),
        collection_id: collectionId,
      });
      // Auto-prepend a required "Item Name" text field if the user didn't add
      // one themselves. Every item uses this as its display title.
      const haveItemName = pendingFields.some(
        (f) => (f.field_name || "").trim().toLowerCase() === "item name"
      );
      const fieldsToSave = haveItemName
        ? pendingFields
        : [{ field_name: "Item Name", field_type: "text", required: true, options: [] }, ...pendingFields];
      // Persist each pending field in order — uses /custom_fields so
      // locked template types (Whisky) accept the writes and record the
      // fields as user-defined (master=false), placed AFTER the CDI
      // master block. Non-locked types treat this identically to the
      // legacy /fields endpoint aside from the master flag.
      for (let i = 0; i < fieldsToSave.length; i++) {
        const f = fieldsToSave[i];
        await apiClient.post(`/categories/${cat.id}/custom_fields`, {
          field_name: f.field_name,
          field_type: f.field_type,
          required: f.required,
          options: f.options || [],
          order: i,
        });
      }
      toast.success(`Category "${cat.category_name}" created.`);
      // Track the most-recently-saved category so the gold guidance
      // panel above can surface a "Manage Fields" deep link to the
      // Manage Fields screen for that category (auto-scrolls to the
      // preset dropdown on arrival).
      setLastSavedCatId(cat.id);
      resetForm();
      await load();
      // If the user came from another flow (Add Item empty-state), send
      // them back so they can pick up where they left off.
      if (returnTo) {
        // Use replace so the back button doesn't bounce them back to the
        // categories form.
        navigate(returnTo, { replace: true });
      }
    } catch (e) {
      const raw = e?.response?.data?.detail;
      const msg = Array.isArray(raw)
        ? raw.map((er) => `${(er.loc || []).slice(-1)[0] || "field"}: ${er.msg}`).join("; ")
        : (typeof raw === "string" ? raw : "Failed to create category.");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditName(c.category_name || "");
    setEditDescription(c.category_description || "");
    setEditCollectionId(c.collection_id || "");
    setEditImage(c.image || "");
    setEditImageDirty(false);
    setConfirmDeleteId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditDescription("");
    setEditCollectionId("");
    setEditImage("");
    setEditImageDirty(false);
  };

  const onEditImagePick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset input
    if (!file) return;
    // Strict image-type gate — only JPG / PNG / WEBP. Mirrors the
    // backend allowlist in `_validate_item_image_types`.
    const ALLOWED_MIMES = new Set(["image/jpeg", "image/png", "image/webp"]);
    const ALLOWED_EXTS  = /\.(jpe?g|png|webp)$/i;
    const isAllowed =
      (file.type && ALLOWED_MIMES.has(file.type.toLowerCase())) ||
      (file.name && ALLOWED_EXTS.test(file.name));
    if (!isAllowed) {
      setEditImageTypeError("Only JPG, PNG, or WEBP images are allowed.");
      return;
    }
    setEditImageTypeError("");
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Image must be smaller than 4 MB.");
      return;
    }
    const r = new FileReader();
    r.onload = () => {
      setEditImage(r.result || "");
      setEditImageDirty(true);
    };
    r.readAsDataURL(file);
  };

  const removeEditImage = () => {
    setEditImage("");
    setEditImageDirty(true);
  };

  const saveEdit = async (id) => {
    if (!editName.trim()) { toast.error("Name is required."); return; }
    if (!editCollectionId) { toast.error("Please pick a collection."); return; }
    setSavingEdit(true);
    try {
      const body = {
        category_name: editName.trim(),
        category_description: editDescription.trim(),
        collection_id: editCollectionId,
      };
      // Only send `image` when the user has changed it. `null` clears the
      // thumbnail on the backend (PUT honours explicit null since the
      // image-unlink fix).
      if (editImageDirty) body.image = editImage ? editImage : null;
      await apiClient.put(`/categories/${id}`, body);
      toast.success("Category updated.");
      cancelEdit();
      await load();
    } catch (e) {
      const raw = e?.response?.data?.detail;
      const msg = Array.isArray(raw)
        ? raw.map((er) => `${(er.loc || []).slice(-1)[0] || "field"}: ${er.msg}`).join("; ")
        : (typeof raw === "string" ? raw : "Failed to save.");
      toast.error(msg);
    } finally {
      setSavingEdit(false);
    }
  };

  const askDelete = (id) => {
    setEditingId(null);
    setConfirmDeleteId(id);
  };

  const cancelDelete = () => setConfirmDeleteId(null);

  const confirmDelete = async (id) => {
    setDeletingId(id);
    // Capture the category name BEFORE the DELETE removes it from state
    // so the digest event reads naturally.
    const catName = cats.find((c) => c.id === id)?.category_name || "Untitled category";
    try {
      await apiClient.delete(`/categories/${id}`);
      toast.success("Category deleted.");
      addCleanupEvent({
        type: "delete",
        itemName: catName,
        fromCategory: null,
        toCategory: null,
      });
      setConfirmDeleteId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    } finally {
      setDeletingId(null);
    }
  };

  const collectionName = (cid) => collections.find((c) => c.id === cid)?.name || "—";

  // Scope the visible categories by selected collection filter (or show all
  // when empty). The grid below uses this list as its SINGLE source of
  // truth — search does NOT mutate it. The autosuggest dropdown is the
  // only surface that renders the filtered list.
  const visibleCats = filterCollectionId
    ? cats.filter((c) => c.collection_id === filterCollectionId)
    : cats;
  // Filtered list used ONLY by the autosuggest dropdown.
  const categorySearchMatches = visibleCats.filter((c) => matchCategorySearch(c, searchInput));
  const categorySuggestions = searchInput.trim()
    ? categorySearchMatches.slice(0, 10)
    : [];

  // Context-aware Back: same resolver as the other detail screens. Falls
  // back to Home since /categories is itself a top-level nav target.
  const backHref = resolveBackHref({
    stateFrom: location.state?.from,
    currentPath: location.pathname + location.search,
    fallback: "/",
  });

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="categories-page">
      <button
        type="button"
        onClick={() => navigate(backHref)}
        data-testid="categories-back"
        aria-label="Back to previous screen"
        className="vp-btn vp-btn-secondary vp-btn-sm mb-3 inline-flex items-center gap-2"
      >
        <ArrowLeft size={14} />
        <span>Back</span>
      </button>
      {/* === Existing categories list === */}
      <section className="mb-14">
        <div className="vp-label text-black dark:text-white mb-2">Categories</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-black dark:text-white mb-4">
          {loading
            ? "…"
            : `${visibleCats.length} categor${visibleCats.length === 1 ? "y" : "ies"}${
                filterCollectionId ? ` in ${collectionName(filterCollectionId)}` : ""
              }`}
        </h1>

        {/* Search bar with autosuggest — filters categories live by name OR
            description as the user types. Clicking a suggestion in the
            dropdown opens that category in the builder. Mirrors the
            Items / Collections page search styling. */}
        <div className="mb-4 relative" ref={searchWrapRef} data-testid="categories-search">
          <Search
            size={16}
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 z-10 text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]"
          />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setSuggestOpen(true);
              setActiveSuggestIdx(-1);
            }}
            onFocus={() => { if (searchInput.trim()) setSuggestOpen(true); }}
            onKeyDown={(e) => {
              if (!suggestOpen || categorySuggestions.length === 0) {
                if (e.key === "Enter" && searchInput.trim() && categorySuggestions.length === 1) {
                  e.preventDefault();
                  openCategorySuggestion(categorySuggestions[0].id);
                }
                return;
              }
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActiveSuggestIdx((i) => Math.min(categorySuggestions.length - 1, i + 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActiveSuggestIdx((i) => Math.max(-1, i - 1));
              } else if (e.key === "Enter") {
                e.preventDefault();
                const pick = activeSuggestIdx >= 0 ? categorySuggestions[activeSuggestIdx] : categorySuggestions[0];
                if (pick) openCategorySuggestion(pick.id);
              }
            }}
            placeholder="Search categories…"
            data-testid="categories-search-input"
            aria-autocomplete="list"
            aria-controls="categories-suggest-list"
            aria-expanded={suggestOpen && categorySuggestions.length > 0}
            className="vp-input w-full !pl-9 !border-[hsl(var(--border-strong))]"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => { setSearchInput(""); setSuggestOpen(false); }}
              data-testid="categories-search-clear"
              aria-label="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 z-10 p-1 rounded hover:bg-[hsl(var(--muted))] vp-muted"
            >
              <X size={14} />
            </button>
          )}
          {suggestOpen && categorySuggestions.length > 0 && (
            <ul
              id="categories-suggest-list"
              role="listbox"
              data-testid="categories-suggest-list"
              className="absolute left-0 right-0 top-full mt-1 z-30 max-h-72 overflow-auto rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elevated))] shadow-lg py-1 vp-fade-in"
            >
              {categorySuggestions.map((c, i) => (
                <li
                  key={c.id}
                  role="option"
                  aria-selected={i === activeSuggestIdx}
                  data-testid={`categories-suggest-${c.id}`}
                  onMouseDown={(e) => { e.preventDefault(); openCategorySuggestion(c.id); }}
                  onMouseEnter={() => setActiveSuggestIdx(i)}
                  className={`px-3 py-2 cursor-pointer flex items-center gap-3 ${
                    i === activeSuggestIdx ? "bg-[hsl(var(--muted))]" : "hover:bg-[hsl(var(--muted))]"
                  }`}
                >
                  <div className="w-7 h-7 rounded bg-[hsl(var(--accent))] text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))] flex items-center justify-center shrink-0">
                    <Tag size={13} strokeWidth={1.75} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{c.category_name}</div>
                    {c.category_description && (
                      <div className="text-xs vp-muted truncate">{c.category_description}</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Filter: scope to a single collection. Categories are NOT global. */}
        <div className="flex items-center gap-3 mb-8 flex-wrap" data-testid="categories-filter-row">
          <label htmlFor="cat-filter-coll" className="text-xs font-medium text-black dark:text-white">
            Filter by collection
          </label>
          <select
            id="cat-filter-coll"
            data-testid="categories-filter-collection"
            value={filterCollectionId}
            onChange={(e) => {
              const v = e.target.value;
              writeFilterCollectionId(v);
              // Pre-fill new-category form's collection to match the filter (better UX).
              if (v) setCollectionId(v);
            }}
            className="vp-select"
          >
            <option value="">All collections</option>
            {collections.map((col) => (
              <option key={col.id} value={col.id}>{col.name}</option>
            ))}
          </select>
          {filterCollectionId && (
            <button
              type="button"
              data-testid="categories-filter-clear"
              onClick={() => writeFilterCollectionId("")}
              className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))]"
            >
              Clear
            </button>
          )}
        </div>

        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => <div key={i} className="vp-card h-16 vp-skeleton" />)}
          </div>
        ) : visibleCats.length === 0 ? (
          <EmptyState
            Icon={Tag}
            title={filterCollectionId ? "No categories in this collection yet" : "No categories yet"}
            body="Create your first category to start organising your collection."
            actionLabel="Create category"
            actionOnClick={() => {
              const el = document.querySelector('[data-testid="categories-form"]');
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                const input = document.querySelector('[data-testid="categories-form-name"]');
                if (input) setTimeout(() => input.focus(), 350);
              }
            }}
            testid="categories-empty"
          />
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 landscape:grid-cols-2 lg:landscape:grid-cols-3 auto-rows-fr gap-4 vp-stagger" data-testid="categories-list">
            {visibleCats.map((c) => (
              <li
                key={c.id}
                data-testid={`category-row-${c.id}`}
                className="vp-card vp-card-hover p-4 sm:p-5 group max-w-full min-h-[7rem] h-full overflow-hidden flex flex-col"
              >
                {editingId === c.id ? (
                  <div className="space-y-3" data-testid={`category-edit-form-${c.id}`}>
                    <div>
                      <label className="block text-xs font-medium mb-1.5 vp-muted">
                        Name <span className="text-[hsl(var(--loss))]">*</span>
                      </label>
                      <input
                        data-testid={`category-edit-name-${c.id}`}
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="vp-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1.5 vp-muted">Description</label>
                      <input
                        data-testid={`category-edit-description-${c.id}`}
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        placeholder="A short description (optional)"
                        className="vp-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1.5 vp-muted">
                        Collection <span className="text-[hsl(var(--loss))]">*</span>
                      </label>
                      <select
                        data-testid={`category-edit-collection-${c.id}`}
                        value={editCollectionId}
                        onChange={(e) => setEditCollectionId(e.target.value)}
                        className="vp-select w-full"
                      >
                        <option value="" disabled>Pick a collection…</option>
                        {collections.map((col) => (
                          <option key={col.id} value={col.id}>{col.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1.5 vp-muted">Photo</label>
                      <div className="flex items-center gap-3" data-testid={`category-edit-image-row-${c.id}`}>
                        <div className="w-12 h-12 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))] overflow-hidden shrink-0 flex items-center justify-center">
                          {editImage ? (
                            <img src={editImage} alt="" className="w-full h-full object-cover" data-testid={`category-edit-image-preview-${c.id}`} />
                          ) : (
                            <ImageIcon size={14} className="vp-subtle" />
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => editImageInputRef.current?.click()}
                          data-testid={`category-edit-upload-${c.id}`}
                          className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))]"
                        >
                          <Upload size={13} /> {editImage ? "Replace Photo" : "Upload Photo"}
                        </button>
                        {editImage && (
                          <button
                            type="button"
                            onClick={removeEditImage}
                            data-testid={`category-edit-remove-image-${c.id}`}
                            className="vp-btn vp-btn-ghost vp-btn-sm text-[hsl(var(--loss))] border border-[hsl(var(--loss))]"
                          >
                            <X size={13} /> Remove Photo
                          </button>
                        )}
                        <input
                          ref={editImageInputRef}
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                          onChange={onEditImagePick}
                          className="hidden"
                          data-testid={`category-edit-image-input-${c.id}`}
                        />
                      </div>
                      {editImageTypeError && (
                        <p data-testid={`category-edit-image-type-error-${c.id}`} className="text-[11px] text-[hsl(var(--loss))] mt-2">
                          {editImageTypeError}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={() => saveEdit(c.id)}
                        disabled={savingEdit || !editName.trim() || !editCollectionId}
                        data-testid={`category-edit-save-${c.id}`}
                        className="vp-btn vp-btn-primary vp-btn-sm disabled:opacity-50"
                      >
                        {savingEdit ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        {savingEdit ? "Saving…" : "Save"}
                      </button>
                      <button
                        onClick={cancelEdit}
                        disabled={savingEdit}
                        data-testid={`category-edit-cancel-${c.id}`}
                        className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))]"
                      >
                        <X size={14} /> Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-start gap-3 max-w-full">
                    <Link
                      to={`/categories/${c.id}/edit`}
                      data-testid={`category-open-${c.id}`}
                      title="Open category in builder"
                      className="flex items-start gap-4 flex-1 min-w-0 basis-full sm:basis-auto max-w-full hover:opacity-90"
                    >
                      <div className="w-10 h-10 rounded-lg bg-[hsl(var(--accent))] text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))] flex items-center justify-center shrink-0 mt-0.5">
                        <Tag size={16} strokeWidth={1.75} />
                      </div>
                      <div className="flex-1 min-w-0 max-w-full">
                        <div
                          className="text-base font-medium tracking-tight truncate max-w-full [overflow-wrap:anywhere]"
                          title={c.category_name}
                        >
                          {c.category_name}
                        </div>
                        {c.category_description && (
                          <div
                            className="text-xs vp-muted mt-1 break-words [overflow-wrap:anywhere] line-clamp-3 max-w-full"
                            data-testid={`category-description-${c.id}`}
                            title={c.category_description}
                          >
                            {c.category_description}
                          </div>
                        )}
                      </div>
                    </Link>
                    {c.collection_id && (
                      <span
                        role="link"
                        tabIndex={0}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(`/collections/${c.collection_id}`);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate(`/collections/${c.collection_id}`);
                          }
                        }}
                        data-testid={`category-collection-${c.id}`}
                        title={`Open ${collectionName(c.collection_id)}`}
                        aria-label={`Return to ${collectionName(c.collection_id)}`}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded bg-[hsl(var(--accent))] text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))] hover:opacity-90 shrink-0 self-start max-w-[60%] cursor-pointer transition-opacity duration-fast"
                      >
                        <Layers size={10} className="shrink-0" /> <span className="truncate">{collectionName(c.collection_id)}</span>
                      </span>
                    )}
                    {confirmDeleteId === c.id ? (
                      <div
                        className="flex items-center gap-1 bg-[hsl(var(--bg-elevated))] border border-[hsl(var(--border))] rounded-md p-1 shadow-sm"
                        data-testid={`category-delete-confirm-${c.id}`}
                      >
                        <button
                          type="button"
                          onClick={() => confirmDelete(c.id)}
                          disabled={deletingId === c.id}
                          data-testid={`category-delete-yes-${c.id}`}
                          className="vp-btn vp-btn-sm !h-8 !px-2 bg-[hsl(var(--loss))] text-white hover:opacity-90 disabled:opacity-50"
                        >
                          {deletingId === c.id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                          <span className="text-xs font-medium">Delete</span>
                        </button>
                        <button
                          type="button"
                          onClick={cancelDelete}
                          data-testid={`category-delete-cancel-${c.id}`}
                          className="vp-btn vp-btn-ghost vp-btn-sm !h-8 !w-8 !p-0 border border-[hsl(var(--border))]"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 shrink-0 self-start ml-auto">
                        <Link
                          to={`/items?category_id=${c.id}`}
                          data-testid={`category-view-items-${c.id}`}
                          aria-label={`View items in ${c.category_name}`}
                          title="View items in this category"
                          className="vp-btn vp-btn-ghost vp-btn-sm !h-9 !w-9 !p-0 shrink-0 border border-[hsl(var(--border))] hover:text-[hsl(var(--gold-strong))] dark:hover:text-[hsl(var(--gold))]"
                        >
                          <Eye size={14} />
                        </Link>
                        <button
                          type="button"
                          onClick={() => startEdit(c)}
                          data-testid={`category-edit-${c.id}`}
                          aria-label={`Edit ${c.category_name}`}
                          title="Edit category inline (name, description, collection, image)"
                          className="vp-btn vp-btn-ghost vp-btn-sm !h-9 !w-9 !p-0 shrink-0 border border-[hsl(var(--border))]"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => duplicate(c.id)}
                          disabled={duplicatingId === c.id}
                          data-testid={`category-duplicate-${c.id}`}
                          aria-label={`Duplicate ${c.category_name}`}
                          title="Duplicate category (clones schema, not items)"
                          className="vp-btn vp-btn-ghost vp-btn-sm !h-9 !w-9 !p-0 shrink-0 border border-[hsl(var(--border))] hover:text-[hsl(var(--gold-strong))] dark:hover:text-[hsl(var(--gold))] disabled:opacity-50"
                        >
                          {duplicatingId === c.id ? <Loader2 size={14} className="animate-spin" /> : <Copy size={14} />}
                        </button>
                        <button
                          onClick={() => askDelete(c.id)}
                          data-testid={`category-delete-${c.id}`}
                          aria-label={`Delete ${c.category_name}`}
                          title="Delete category"
                          className="vp-btn vp-btn-ghost vp-btn-sm !h-9 !w-9 !p-0 shrink-0 border border-[hsl(var(--loss))] text-[hsl(var(--loss))]"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* === Create new category === */}
      <section className="vp-card p-6 sm:p-7" data-testid="categories-form">
        <h2 className="text-lg font-semibold mb-1">Create a new category</h2>
        <p className="text-xs vp-muted mb-5">Name your category and choose a collection.</p>

        {/* Presets guidance — Wine / Whisky / Spirits master templates
            are applied AFTER save (from the Manage Fields screen). This
            panel exists so users don't hunt for a preset dropdown here. */}
        <div
          data-testid="categories-form-preset-info"
          className="mb-4 rounded-lg border border-[hsl(var(--gold))]/40 bg-[hsla(35,47%,60%,0.06)] px-4 py-3"
        >
          <p className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--gold))] mb-2">
            Using Wine, Whisky, or Spirits presets.
          </p>
          <p className="text-xs vp-muted leading-relaxed mb-2">
            Preset fields are applied automatically when you select a Wine, Whisky, or Spirits collection.
          </p>
          <p className="text-xs vp-muted leading-relaxed mb-2">
            These preset fields are locked and cannot be edited or deleted.
          </p>
          <p className="text-xs vp-muted leading-relaxed">
            You can still add custom fields below if needed.
          </p>
        </div>

        {/* Custom category guidance — for users who want a bespoke
            schema instead of the CDI templates. Custom fields added
            below travel with the category on save. */}
        <div
          data-testid="categories-form-custom-info"
          className="mb-6 rounded-lg border border-[hsl(var(--border))] px-4 py-3"
        >
          <div className="text-xs font-semibold uppercase tracking-wider vp-muted mb-1">
            Building a custom category
          </div>
          <p className="text-xs vp-muted leading-relaxed">
            All categories use the preset fields from their selected collection.
            You can add your own custom fields below to extend the category.
            Custom fields can be edited, reordered, or deleted at any time.
            Custom categories do not replace Wine, Whisky, or Spirits presets—they build on top of them.
          </p>
        </div>

        <div className="space-y-4 mb-5">
          <div>
            <label htmlFor="cat-new-name" className="block text-xs font-medium mb-1.5 vp-muted">
              Category Name <span className="text-[hsl(var(--loss))]">*</span>
            </label>
            <input
              id="cat-new-name"
              data-testid="categories-form-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Bordeaux, Islay, Tequila"
              className="vp-input"
            />
          </div>
          <div>
            <label htmlFor="cat-new-collection" className="block text-xs font-medium mb-1.5 vp-muted">
              Collection <span className="text-[hsl(var(--loss))]">*</span>
            </label>
            <select
              id="cat-new-collection"
              data-testid="categories-form-collection"
              value={collectionId}
              onChange={(e) => onCollectionChange(e.target.value)}
              className="vp-select w-full"
            >
              <option value="" disabled>Pick a collection…</option>
              {collections.map((col) => (
                <option key={col.id} value={col.id}>{col.name}</option>
              ))}
            </select>
            {presetKey === "wine" && (
              <p className="preset-note">Wine preset applied automatically from the selected collection.</p>
            )}
            {presetKey === "whisky" && (
              <p className="preset-note">Whisky preset applied automatically from the selected collection.</p>
            )}
            {presetKey === "spirits" && (
              <p className="preset-note">Spirits preset applied automatically from the selected collection.</p>
            )}
          </div>
          <div>
            <label htmlFor="cat-new-desc" className="block text-xs font-medium mb-1.5 vp-muted">Description</label>
            <input
              id="cat-new-desc"
              data-testid="categories-form-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A short description (optional)"
              className="vp-input"
            />
          </div>
        </div>

        {/* Pending fields list */}
        {false && pendingFields.length > 0 && (
          <div className="mb-4 border border-[hsl(var(--border))] rounded-lg overflow-hidden" data-testid="categories-form-fields">
            <div className="px-4 py-2 vp-label bg-[hsl(var(--muted))] border-b border-[hsl(var(--border))]">
              {pendingFields.length} field{pendingFields.length === 1 ? "" : "s"}
            </div>
            <ul>
              {false && pendingFields.map((f, idx) => {
                const t = FIELD_TYPES.find((x) => x.value === f.field_type);
                const Icon = t?.Icon || Type;
                return (
                  <li
                    key={idx}
                    data-testid={`categories-form-field-${idx}`}
                    className="flex items-center gap-3 px-4 py-2.5 border-b border-[hsl(var(--border))] last:border-b-0 text-sm"
                  >
                    <Icon size={13} className="vp-muted shrink-0" />
                    <span className="flex-1 min-w-0 truncate">{f.field_name}</span>
                    <span className="text-xs vp-muted">{t?.label || f.field_type}</span>
                    {f.required && (
                      <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--loss))] font-medium px-1.5 py-0.5 rounded bg-[hsl(var(--loss))]/10">
                        Required
                      </span>
                    )}
                    {f.isPreset !== true && (
                      <button
                        onClick={() => removePendingField(idx)}
                        data-testid={`categories-form-field-${idx}-remove`}
                        aria-label="Remove field"
                        className="vp-btn vp-btn-ghost vp-btn-sm !h-7 !w-7 !p-0 text-[hsl(var(--loss))]"
                      >
                        <X size={13} />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="flex flex-wrap justify-between gap-2 items-center">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => {
                // Guardrail: require both Category Name and Collection
                // before the Add Field dialog can open. Matches the
                // Save Category validation gate so users don't queue
                // fields into an incomplete category shell.
                if (!name.trim() || !collectionId) {
                  toast.error("Please enter a Category Name and choose a Collection before adding fields.");
                  return;
                }
                setShowFieldModal(true);
              }}
              data-testid="categories-add-field"
              className="vp-btn vp-btn-secondary vp-btn-sm"
            >
              <Plus size={14} /> Add Field
            </button>
            {/* Preset dropdown removed here per CDI policy — presets are
                only applied AFTER category save from the Manage Fields
                screen. See the gold guidance panel above. */}
          </div>
          <button
            type="button"
            onClick={saveCategory}
            disabled={createSaveDisabled}
            data-testid="categories-save"
            title={
              !name.trim() ? "Enter a category name to enable Save"
              : !collectionId ? "Pick a collection to enable Save"
              : "Save this category" + (pendingFields.length > 0 ? " and its custom fields" : "")
            }
            className="vp-btn vp-btn-primary vp-btn-sm disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? "Saving…" : "Save Category"}
          </button>
        </div>
      </section>

      {showFieldModal && (
        <FieldModal
          onCancel={() => setShowFieldModal(false)}
          onAdd={addPendingField}
        />
      )}

      <BottomNav current="categories" />
    </div>
  );
}

/* ============================================================
   Add-Field modal — fields: name, type, required (+ options if dropdown).
   ============================================================ */
function FieldModal({ onCancel, onAdd }) {
  const [fieldName, setFieldName] = useState("");
  const [fieldType, setFieldType] = useState("text");
  const [required, setRequired] = useState(false);
  const [optionsText, setOptionsText] = useState("");

  const needsOptions = fieldType === "dropdown" || fieldType === "multi_select";

  const handleAdd = () => {
    if (!fieldName.trim()) { toast.error("Field name is required."); return; }
    const options = needsOptions
      ? optionsText.split("\n").map((s) => s.trim()).filter(Boolean)
      : [];
    if (needsOptions && options.length === 0) {
      toast.error("Add at least one option for dropdown / multi-select."); return;
    }
    onAdd({
      field_name: fieldName.trim(),
      field_type: fieldType,
      required,
      options,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center vp-fade-bg"
      role="dialog"
      aria-modal="true"
      aria-labelledby="field-modal-title"
    >
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div
        data-testid="field-modal"
        className="relative z-10 w-full sm:max-w-md mx-0 sm:mx-4 bg-[hsl(var(--bg-elevated))] border border-[hsl(var(--border))] rounded-t-2xl sm:rounded-xl shadow-vp-pop vp-fade-in"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[hsl(var(--border))]">
          <h3 id="field-modal-title" className="text-lg font-semibold tracking-tight">Add Field</h3>
          <button
            onClick={onCancel}
            data-testid="field-modal-close"
            aria-label="Close"
            className="vp-btn vp-btn-ghost vp-btn-sm !h-8 !w-8 !p-0"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label htmlFor="fm-name" className="block text-xs font-medium mb-1.5 vp-muted">
              Field Name <span className="text-[hsl(var(--loss))]">*</span>
            </label>
            <input
              id="fm-name"
              data-testid="field-modal-name"
              value={fieldName}
              onChange={(e) => setFieldName(e.target.value)}
              placeholder="e.g. Brand"
              className="vp-input"
              autoFocus
            />
          </div>

          <div>
            <label htmlFor="fm-type" className="block text-xs font-medium mb-1.5 vp-muted">Field Type</label>
            <select
              id="fm-type"
              data-testid="field-modal-type"
              value={fieldType}
              onChange={(e) => setFieldType(e.target.value)}
              className="vp-select w-full"
            >
              {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          {needsOptions && (
            <div>
              <label htmlFor="fm-options" className="block text-xs font-medium mb-1.5 vp-muted">
                Options (one per line)
              </label>
              <textarea
                id="fm-options"
                data-testid="field-modal-options"
                rows={4}
                value={optionsText}
                onChange={(e) => setOptionsText(e.target.value)}
                placeholder={"Mint\nExcellent\nGood\nFair\nPoor"}
                className="vp-textarea w-full !text-sm font-mono"
              />
            </div>
          )}

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="field-modal-required"
              checked={required}
              onChange={(e) => setRequired(e.target.checked)}
              className="rounded accent-[hsl(var(--gold))]"
            />
            <span className="text-sm">Required field</span>
          </label>
        </div>

        <div className="flex justify-end gap-2 p-5 pt-0">
          <button
            type="button"
            onClick={onCancel}
            data-testid="field-modal-cancel"
            className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAdd}
            data-testid="field-modal-add"
            className="vp-btn vp-btn-primary vp-btn-sm"
          >
            <Plus size={14} /> Add Field
          </button>
        </div>
      </div>
    </div>
  );
}
